import {getVideoId, getVideoThumbnailURL, getVideoType} from '@Libraries/embedded-video-library';
import {attachTypingStoppedHandler} from '@Utils/browser.util';
import type {Component, PropertyProps} from 'grapesjs';
import type {PrimaryConfigOptions} from '@/email-maker/grapesjs-email-editor-plugin/src';

export interface EmailEditorLinkWrapperProps extends PropertyProps {
  component: Component;
}

export default (opt: PrimaryConfigOptions): void => {
  const {editor} = opt;
  const styleManager = editor.StyleManager;

  styleManager.addType(window.PMW.emails.styles.ID_LINK_WRAPPER, {
    create(data) {
      const {change} = data;
      const props = data.props as EmailEditorLinkWrapperProps;
      props.component = props.component || editor.getSelected();

      const container = document.createElement('div');
      const label = document.createElement('span');
      const inputDiv = document.createElement('div');
      const icon = document.createElement('i');
      const el = document.createElement('input');
      const attributes = props.component.getAttributes() || {};
      const errorMessageContainer = createErrorMessage();

      el.setAttribute('type', 'url');
      el.setAttribute('class', 'pmw-email-url-input js-toggle-link-new-tab -selected-comp');
      el.setAttribute('maxlength', '2000');
      el.setAttribute('placeholder', window.i18next.t('pmwjs_link_placeholder'));
      el.setAttribute('aria-label', window.i18next.t('pmwjs_link_placeholder'));
      el.setAttribute('data-preset-id', window.PMW.emails.TOGGLE_OPTION_PRESET_ID_ONE.toString());

      errorMessageContainer.classList.add('_hidden');

      label.innerHTML = window.i18next.t('pmwjs_link');
      label.className = 'pmw-link-wrapper-label body-sm-bold content-body';

      inputDiv.className = 'input body-md-regular url-input-container hover-transition-all';
      icon.className = 'pmw-icon icon-link size-icon-16';

      container.className = 'contextual-input';
      container.appendChild(label);
      container.appendChild(inputDiv);
      container.appendChild(errorMessageContainer);

      inputDiv.appendChild(icon);
      inputDiv.appendChild(el);

      if (attributes['data-url'] && typeof attributes['data-url'] === 'string') {
        el.setAttribute('data-preset-id', window.PMW.emails.TOGGLE_OPTION_PRESET_ID_ZERO.toString());
        el.setAttribute('value', attributes['data-url']);
      }

      attachTypingStoppedHandler(
        $(el),
        (e) => {
          return change({el, e});
        },
        1000
      );

      const signupFormlinker = createSignupFormLinker(el.value);
      if (!signupFormlinker) {
        return container;
      }

      signupFormlinker.classList.add('_hidden');
      container.appendChild(signupFormlinker);

      createEventHandlersForSignupFormLinker(signupFormlinker, el, inputDiv);

      return container;
    },
    emit(data, {el, event, complete}: {el: HTMLInputElement; event: Event; complete: () => void}) {
      const props = data.props as EmailEditorLinkWrapperProps;
      const value = editor.runCommand(window.PMW.emails.commands.GET_VALIDATED_URL, {
        url: el.value,
      }) as string;

      el.value = value;

      if (
        editor.runCommand(window.PMW.emails.commands.IS_VIDEO_LINK_WRAPPER, {
          component: props.component,
        })
      ) {
        const videoId = getVideoId(value);
        const videoType = getVideoType(value);

        if (!videoType || !videoId) {
          window.PMW.openErrorModal({message: window.i18next.t('pmwjs_error_try_again_reload_page')});
          editor.runCommand(window.PMW.emails.commands.SET_IMAGE_LOADING, {component: props.component, loading: false});
          return;
        }

        editor.runCommand(window.PMW.emails.commands.SET_IMAGE_LOADING, {
          component: props.component,
          loading: true,
          text: window.i18next.t('pmwjs_loading'),
        });

        getVideoThumbnailURL(videoId, videoType)
          .then((response) => {
            editor.runCommand(window.PMW.emails.commands.SET_EMBEDDED_VIDEO_SRC, {
              component: props.component,
              extension: 'jpg',
              thumbnailURL: response,
              videoURL: value,
            });
          })
          .catch((e) => {
            window.PMW.openErrorModal({message: e || window.i18next.t('pmwjs_error_try_again_reload_page')});
            editor.runCommand(window.PMW.emails.commands.SET_IMAGE_LOADING, {
              component: props.component,
              loading: false,
            });
          });

        return;
      }

      editor.runCommand(window.PMW.emails.commands.ADD_COMPONENT_ANCHOR, {
        component: props.component,
        url: value,
      });

      if (value.length === 0) {
        editor.runCommand(window.PMW.emails.commands.REMOVE_COMPONENT_ANCHOR, {
          component: props.component,
        });
      }

      if (
        el.value === '' ||
        editor.runCommand(window.PMW.emails.commands.IS_VALID_FOR_LINK, {
          url: el.value,
        })
      ) {
        doShowInvalidUrlMessage(false);
      } else {
        doShowInvalidUrlMessage(true);
      }

      if (
        editor.runCommand(window.PMW.emails.commands.IS_VALID_URL, {
          url: el.value,
        })
      ) {
        el.setAttribute('data-preset-id', window.PMW.emails.TOGGLE_OPTION_PRESET_ID_ZERO.toString());
      } else {
        el.setAttribute('data-preset-id', window.PMW.emails.TOGGLE_OPTION_PRESET_ID_ONE.toString());
      }

      editor.runCommand(window.PMW.emails.commands.INIT_COLLAPSED_STYLES);
    },
    update({value, el}) {
      autoFillLinkInput(el);
    },

    destroy() {
      document.removeEventListener('click', clickOutsideListener);
    },
  });

  function doShowInvalidUrlMessage(state: boolean): void {
    const invalidUrlMessage = document.querySelector('.error-message-container') as HTMLDivElement;
    const urlContainer = document.querySelector('.url-input-container') as HTMLDivElement;
    const signupFormLinker = document.querySelector('.signup-form-linker') as HTMLDivElement;

    if (state) {
      invalidUrlMessage.classList.remove('_hidden');
      urlContainer.classList.add('-error');
      signupFormLinker.classList.add('_hidden');
    } else {
      invalidUrlMessage.classList.add('_hidden');
      urlContainer.classList.remove('-error');
    }
  }

  function createErrorMessage(): HTMLDivElement {
    const errorMessageContainer = document.createElement('div');
    const errorMessage = document.createElement('span');
    const errorIcon = document.createElement('i');

    errorMessageContainer.className = 'error-message-container';
    errorMessage.innerHTML = window.i18next.t('pmwjs_invalid_format');
    errorMessage.className = 'invalid-url-message';
    errorIcon.className = 'pmw-icon icon-exclamation-triangle size-icon-16 _unmargin';

    errorMessageContainer.appendChild(errorIcon);
    errorMessageContainer.appendChild(errorMessage);

    return errorMessageContainer;
  }

  function createSignupFormLinker(preSelectedUrl: string): HTMLDivElement | null {
    const signupFormLinks = getSignupFormLinks(preSelectedUrl);

    if (signupFormLinks.length === 0) {
      return null;
    }

    const dropdown = document.createElement('div');
    const list = document.createElement('ul');
    const header = document.createElement('li');
    const loader = document.createElement('progress');

    dropdown.className = 'signup-form-linker';
    list.className = 'signup-form-list';
    header.className = 'signup-form-header';
    loader.className = 'signup-form-loader icon-circular-loader';
    header.innerHTML = window.i18next.t('pmwjs_suggested_links');

    list.appendChild(header);

    signupFormLinks.forEach((link) => {
      list.appendChild(link);
    });

    list.appendChild(loader);
    dropdown.appendChild(list);

    return dropdown;
  }

  function getSignupFormLinks(preSelectedUrl: string): HTMLLIElement[] {
    const signupFormLinks: HTMLLIElement[] = [];

    const {signupForms} = window.PMW.emails.SIGNUP_FORMS_DATA;
    signupForms.forEach((link) => {
      const listItem = getSignupFormRowHTML(link.name, window.i18next.t('pmwjs_signup_form'));

      if (link.url === preSelectedUrl) {
        listItem.classList.add('-selected');
      }

      listItem.addEventListener('click', () => {
        const siblings = listItem.parentElement?.children;
        if (siblings) {
          for (let i = 0; i < siblings.length; i++) {
            siblings[i].classList.remove('-selected');
          }
        }
        listItem.classList.add('-selected');

        const input = document.querySelector('.pmw-email-url-input') as HTMLInputElement;
        const signupFormlinker = document.querySelector('.signup-form-linker') as HTMLDivElement;

        input.value = link.url;
        input.dispatchEvent(new Event('paste'));
        signupFormlinker.classList.add('_hidden');
        doShowInvalidUrlMessage(false);
      });

      signupFormLinks.push(listItem);
    });

    return signupFormLinks;
  }

  function getSignupFormRowHTML(heading: string, description: string): HTMLLIElement {
    const listItem = document.createElement('li');
    const container = document.createElement('a');
    const iconContainer = document.createElement('div');
    const icon = document.createElement('i');
    const textContainer = document.createElement('div');
    const headingContainer = document.createElement('div');
    const descriptionContainer = document.createElement('div');

    listItem.className = 'signup-form-list-item';
    container.className = 'signup-form-link';
    iconContainer.className = 'signup-form-icon-container';
    icon.className = 'pmw-icon size-icon-24 icon-signup-form';
    textContainer.className = 'signup-form-text-container';
    headingContainer.className = 'signup-form-heading';
    descriptionContainer.className = 'signup-form-description';

    listItem.title = heading;
    headingContainer.textContent = heading;
    descriptionContainer.textContent = description;

    iconContainer.appendChild(icon);
    textContainer.appendChild(headingContainer);
    textContainer.appendChild(descriptionContainer);
    container.appendChild(iconContainer);
    container.appendChild(textContainer);
    listItem.appendChild(container);

    return listItem;
  }

  function autoFillLinkInput(el: HTMLElement): void {
    const input = el.querySelector('.pmw-email-url-input') as HTMLInputElement;
    const urlVal = input.value;

    if (urlVal !== '' && urlVal !== null) {
      input.focus();
    }
  }

  function createEventHandlersForSignupFormLinker(signupFormlinker: HTMLDivElement, inputElement: HTMLInputElement, inputDiv: HTMLDivElement): void {
    inputElement.addEventListener('focus', () => {
      signupFormlinker.classList.remove('_hidden');
      inputDiv.classList.add('-selected');

      const dropdown = document.querySelector('.signup-form-linker') as HTMLDivElement;
      const selectedLink = signupFormlinker.querySelector('.-selected') as HTMLLIElement;
      if (selectedLink) {
        const amount = selectedLink.offsetTop - dropdown.offsetTop + selectedLink.offsetHeight * 2;
        dropdown.scrollTo({
          top: amount,
          behavior: 'smooth',
        });
      }

      addLazyLoader(dropdown);
      hideSignupFormLinkerOffscreen(inputElement, signupFormlinker);
    });

    inputElement.addEventListener('input', () => {
      const signupFormLinks = signupFormlinker.querySelectorAll('.signup-form-list-item');
      signupFormLinks.forEach((link) => {
        link.classList.remove('-selected');
      });
    });

    document.addEventListener('click', clickOutsideListener);
  }

  function addLazyLoader(dropdown: HTMLDivElement): void {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            fetchMoreData();
          }
        });
      },
      {
        root: dropdown,
        threshold: 1.0,
      }
    );

    const observerElement = document.querySelector('.signup-form-list-item:nth-last-child(2)') as HTMLLIElement;
    if (observerElement) {
      observer.observe(observerElement);
    }
  }

  function hideSignupFormLinkerOffscreen(inputElement: HTMLInputElement, signupFormLinker: HTMLDivElement): void {
    const urlContainer = document.querySelector('.url-input-container') as HTMLDivElement;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) {
            signupFormLinker.classList.add('_hidden');
            urlContainer.classList.remove('-selected');
            inputElement.blur();
          }
        });
      },
      {
        root: null,
        threshold: 0,
      }
    );

    observer.observe(inputElement);
  }

  function fetchMoreData(): void {
    const loader = document.querySelector('.signup-form-loader') as HTMLLIElement;
    const dropdownItems = document.querySelector('.signup-form-list') as HTMLUListElement;

    loader.classList.remove('_hidden');

    editor.runCommand(window.PMW.emails.commands.ASYNC_GET_SIGNUP_FORMS, {
      callback: (newItemsStartIndex: number) => {
        const {signupForms} = window.PMW.emails.SIGNUP_FORMS_DATA;
        for (let i = newItemsStartIndex; i < signupForms.length; i++) {
          const newItem = getSignupFormRowHTML(signupForms[i].name, window.i18next.t('pmwjs_signup_form'));
          dropdownItems.insertBefore(newItem, loader);
        }
        loader.classList.add('_hidden');
      },
    });
  }

  function clickOutsideListener(e: MouseEvent): void {
    const el = document.querySelector('.pmw-email-url-input') as HTMLInputElement;
    const signupFormlinker = document.querySelector('.signup-form-linker') as HTMLDivElement;
    if ((signupFormlinker && signupFormlinker.contains(e.target as Node)) || (el && el.contains(e.target as Node))) {
      return;
    }

    const inputContainer = document.querySelector('.url-input-container') as HTMLDivElement;
    signupFormlinker.classList.add('_hidden');
    inputContainer.classList.remove('-selected');
  }
};
